import { datalabelFont, tooltipTitleFont } from "./chartJsPlugins";
import type { Chart, Plugin, LegendItem } from 'chart.js';
import { infoBubble } from "../../helpers/infoBubble";

// accounts for No Data and Fewer than 10
const getFilteredLabel = (value, dataset) => {
    if(value && isNaN(value)) {
        return dataset.label + " - " + value;
    }
    return parseFloat(value) + '%';
}

export const topLabels = {
    id: 'topLabels',
    afterDatasetsDraw(chart, args, options) {
        const { ctx, scales: { x, y } } = chart;
        let isWide = chart.width > 505;

        if (isWide) {
            ctx.font = "400 12px \"Roboto\", Roboto, Arial, sans-serif";
        } else {
            ctx.font = "400 9px \"Roboto\", Roboto, Arial, sans-serif";
        }

        const yAxis = chart.scales.y;
        let label = yAxis.ticks[0];
        let labelWidth = ctx.measureText(label).width;

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle'; // This centers the text vertically on the Y point

        chart.data.datasets.forEach((dataset, index) => {
            if (chart.isDatasetVisible(index)) {
                dataset.data.forEach((item, i) => {
                    let percent = dataset.data[i];
                    let text = getFilteredLabel(percent, dataset);

                    const meta = chart.getDatasetMeta(index).data[i] as any;
                    let xValue = meta.x;
                    let yValue = meta.y; // The center point of the bar/element

                    if (!xValue) {
                        xValue = chart.chartArea.left + labelWidth;
                    }

                    // Percentage label outside the bar at the right edge
                    ctx.fillText(text, xValue + 27, yValue);

                    // Count label (e.g. "1,413") drawn inside the bar at its horizontal center.
                    // chartjs-plugin-datalabels mispositions these labels in Chart.js v4 for
                    // horizontal bar charts, so we draw them directly on the canvas instead.
                    const centerLabel = (dataset as any)._centerLabels?.[i];
                    if (centerLabel != null && centerLabel !== '') {
                        const xBase = typeof meta.base === 'number'
                            ? meta.base
                            : (chart.scales.x as any).getPixelForValue(0);
                        const xCenter = (xValue + xBase) / 2;
                        const bgColor = (Array.isArray(dataset.backgroundColor)
                            ? dataset.backgroundColor[i]
                            : dataset.backgroundColor) as string;
                        ctx.save();
                        ctx.fillStyle = getContrastColor(bgColor ?? '#ffffff');
                        ctx.fillText(centerLabel, xCenter, yValue);
                        ctx.restore();
                    }
                })
            }
        })
    }
}

export const ethnicityTopLabels = {
    id: 'ethnicityTopLabels',
    afterDatasetsDraw(chart, args, options) {
        const { ctx, scales: { x, y } } = chart;
        let isWide = chart.width > 505;
        if (isWide) {
            ctx.font = "400 12px \"Roboto\", Roboto, Arial, sans-serif";
        } else {
            ctx.font = "400 9px \"Roboto\", Roboto, Arial, sans-serif";
        }
        const yAxis = chart.scales.y;
        let label = yAxis.ticks[0];
        let labelWidth = ctx.measureText(label).width;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle'; // This centers the text vertically on the Y point
        chart.data.datasets.forEach((dataset, index) => {
            if (chart.isDatasetVisible(index)) {
                dataset.data.forEach((item, i) => {
                    dataset.data.forEach((item, i) => {
                        let percent = dataset.data[i];
                        const text = getFilteredLabel(percent, dataset);
                        let xValue = chart.getDatasetMeta(index).data[i].x;
                        if (!xValue) {
                            xValue = chart.chartArea.left + labelWidth;
                        }

                        ctx.fillText(text, xValue + 27, chart.getDatasetMeta(index).data[i].y);
                    })
                })
            }

        })
    }
}

export const topLabelsTrends = {
    id: 'topLabelsTrends',
    afterDatasetsDraw(chart, args, options) {
        const { ctx, scales: { x, y } } = chart;
        let isWide = chart.width > 505;
        if (isWide) {
            ctx.font = "500 12px \"Roboto\", Roboto, Arial, sans-serif";
        } else {
            ctx.font = "500 9px \"Roboto\", Roboto, Arial, sans-serif";
        }
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle'; // This centers the text vertically on the Y point
        chart.data.datasets.forEach((dataset, index) => {
            //if (chart.canvas.id.includes('ethnicity2') || chart.canvas.id.includes('ethnicity1')) {
            //    console.log('topLabelsTrends', chart.canvas.id, chart)
            //}
            if (chart.isDatasetVisible(index)) {
                dataset.data.forEach((item, i) => {
                    let percent = dataset.data[i];
                    let text = getFilteredLabel(percent, dataset);
                    let x = chart.getDatasetMeta(index).data[i].x;
                    
                    if (isNaN(x)) {
                        // this is a hack for now. hardcoded offsets for known things, and best effort on others

                        if (text.includes('Fewer than 10 students')) {
                            x = 160;
                        } else if(text.includes('No Data')) {
                            x = 110;
                        } else {
                            x = text.length * 7;
                        }
                    }
                    ctx.fillText(text, x + 25, chart.getDatasetMeta(index).data[i].y);
                    
                })
            }
        })
    }
}

interface HtmlLegendPluginOptions {
    containerID: string;
    tooltip?: string;
    // info bubble content per legend item, keyed by dataset label (eg. School / Network / District)
    itemTooltips?: Record<string, string>;
}

const getOrCreateLegendList = (chart: Chart, option: HtmlLegendPluginOptions): HTMLUListElement => {
    let legendContainer = document.getElementById(option.containerID);

    // Create container if it doesn't exist
    if (!legendContainer) {
        legendContainer = document.createElement('div');
        legendContainer.id = option.containerID;
        legendContainer.className = 'html-legend-plugin__container';

        // Insert the container after the chart canvas
        const canvas = chart.canvas;
        if (canvas.parentNode) {
            canvas.parentNode.insertBefore(legendContainer, canvas);
        } else {
            document.body.appendChild(legendContainer);
        }
      
    }

    let listContainer = legendContainer.querySelector<HTMLUListElement>('ul');

    if (!listContainer) {
        // 1. Create label text
        const labelText = document.createElement('span');
        labelText.className = 'html-legend-plugin__label-text';
        labelText.textContent = 'Compare';
        legendContainer.appendChild(labelText);

        // 2. Create the Tooltip Span
        if (!option.tooltip || option.tooltip === '') {
            option.tooltip = 'All students enrolled at this school who are eligible to be tested.'
        }

        // 3. Create the list container
        listContainer = document.createElement('ul');
        listContainer.className = 'html-legend-plugin__list';
        legendContainer.appendChild(listContainer);
    }

    return listContainer;
};


export const getContrastColor = (color: string): string => {
    // 1. Create a dummy element to let the browser parse the color for us
    // This handles hex, rgb, and named colors (like "red")
    const tempElement = document.createElement("div");
    tempElement.style.color = color;
    document.body.appendChild(tempElement);
    const computedColor = window.getComputedStyle(tempElement).color;
    document.body.removeChild(tempElement);

    // 2. Extract R, G, and B values
    const rgb = computedColor.match(/\d+/g)?.map(Number);
    if (!rgb) return '#000000'; // Fallback

    // 3. Calculate brightness (YIQ formula)
    // Formula: (R * 299 + G * 587 + B * 114) / 1000
    const brightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;

    // 4. If brightness > 128, the background is light (return black), else return white
    return brightness > 128 ? '#000000' : '#ffffff';
};


export const htmlLegendPlugin: Plugin<'line' | 'bar' | 'pie' | 'doughnut'> = {
    id: 'htmlLegend',
    afterUpdate(chart: any, args: any, options: any) {
        if(!options || !options.containerID) return;

        const ul = getOrCreateLegendList(chart, options);
        ul.innerHTML = ''; // Efficiently clear list

        const items = chart.options.plugins.legend.labels.generateLabels(chart);

        items.forEach((item: any) => {
            const li = document.createElement('li');
            li.className = 'html-legend-plugin__item';

            // unique ID for the checkbox to link with <label>
            const checkboxId = `cb-${chart.id}-${item.datasetIndex}`;

            // Create Checkbox
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.className = 'html-legend-plugin__checkbox';
            checkbox.id = checkboxId;
            checkbox.checked = !item.hidden;

            checkbox.onclick = () => {
                chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
                chart.update();
            };

            // Create Label
            const label = document.createElement('label');
            label.className = 'html-legend-plugin__label';
            label.setAttribute('for', checkboxId);
            label.textContent = item.text;

            // Apply your color logic via CSS variables
            const bgColor = item.fillStyle;
            label.style.setProperty('--legend-bg', bgColor);
            label.style.setProperty('--checkmark-color', getContrastColor(bgColor));

            li.appendChild(checkbox);
            li.appendChild(label);

            // optional info bubble for this legend item, same markup/behaviour as the server-rendered ones
            const tipContent = options.itemTooltips?.[item.text];
            if (tipContent && tipContent.trim() !== '') {
                const bubble = document.createElement('span');
                bubble.className = 'school-info-bubble fa fa-info-circle';
                bubble.setAttribute('data-toggle', 'school-profile-tooltip');
                bubble.setAttribute('data-html', 'true');
                bubble.setAttribute('data-tooltip-content', tipContent);
                bubble.setAttribute('aria-label', 'More Info: Click here');
                li.appendChild(bubble);
                infoBubble(bubble);
            }

            ul.appendChild(li);
        });
    }
};


const generateSlug = (length = 6) => {
    return Math.random().toString(36).substring(2, 2 + length);
};

export const generateChartId = (label: string, key: string): string => {
    const slug = label.replace(/\s+/g, '-').toLowerCase();
    return `${key}-${slug}-${generateSlug()}`;
}